import { useEffect, useState, useCallback, useMemo } from 'react';
import type { Threshold } from '@/modules/shared/DataLayers/types';

/**
 * RawSensorData represents incoming backend format, where thresholds may be incomplete.
 */
interface RawSensorData {
  displayName: string;
  thresholds?: Partial<Threshold>[];
  y?: number[]; // Y values for dynamic threshold calculation
}

/**
 * Transforms a raw threshold object into a fully-populated Threshold instance.
 * Ensures defaults are set for missing values.
 *
 * @param sensorName - The display name of the sensor
 * @param raw - A potentially incomplete threshold object
 * @param index - Index used to generate a unique ID
 * @returns A normalized Threshold object
 */
const normalizeThreshold = (sensorName: string, raw: Partial<Threshold>, index: number): Threshold => ({
  id: `${sensorName}-threshold-${index}`,
  sensorName,
  value: raw.value ?? 0,
  color: raw.color ?? '#CC0000',
  label: raw.label ?? '',
  visible: raw.visible ?? true,
  chartType: raw.chartType ?? 'default',
  dash: 'dot',
});

/**
 * Performs a deep comparison of two threshold maps to detect changes.
 * Returns true if the content is equal, false otherwise.
 */
const areThresholdMapsEqual = (a: Record<string, Threshold[]>, b: Record<string, Threshold[]>): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    const aList = a[key];
    const bList = b[key];
    if (!bList || aList.length !== bList.length) return false;

    for (let i = 0; i < aList.length; i++) {
      if (JSON.stringify(aList[i]) !== JSON.stringify(bList[i])) {
        return false;
      }
    }
  }

  return true;
};

/**
 * useThresholds
 *
 * React hook to manage sensor thresholds. Converts raw sensor data into structured
 * threshold records and exposes CRUD-style operations to modify them.
 *
 * @param sensorDataArray - Array of sensor data containing optional raw threshold definitions
 * @returns {
 *   thresholdsBySensor: { [sensorName]: Threshold[] },
 *   allThresholds: Threshold[],
 *   addThreshold: (sensorName, threshold) => void,
 *   removeThreshold: (sensorName, id) => void,
 *   updateThreshold: (sensorName, id, updates) => void
 * }
 */
// Dynamic thresholds based on data range for testing bands visualization
const createDynamicThresholds = (sensorName: string, yValues: number[]): Threshold[] => {
  // Filter out null/undefined/NaN values
  const validValues = yValues.filter((v) => v !== null && v !== undefined && !isNaN(v));
  if (validValues.length === 0) return [];

  const min = Math.min(...validValues);
  const max = Math.max(...validValues);
  const range = max - min;

  // Calculate thresholds as percentages of the range
  // Critical: 15%, Alarm: 30%, Monitor: 50%, Healthy: 80%
  return [
    {
      id: `${sensorName}-threshold-critical`,
      sensorName,
      value: Math.round((min + range * 0.15) * 100) / 100,
      color: '#ef4444', // red
      label: 'Critical',
      visible: true,
      chartType: 'default',
      dash: 'dash',
    },
    {
      id: `${sensorName}-threshold-alarm`,
      sensorName,
      value: Math.round((min + range * 0.30) * 100) / 100,
      color: '#f97316', // orange
      label: 'Alarm',
      visible: true,
      chartType: 'default',
      dash: 'dash',
    },
    {
      id: `${sensorName}-threshold-monitor`,
      sensorName,
      value: Math.round((min + range * 0.50) * 100) / 100,
      color: '#eab308', // yellow
      label: 'Monitor',
      visible: true,
      chartType: 'default',
      dash: 'dash',
    },
    {
      id: `${sensorName}-threshold-healthy`,
      sensorName,
      value: Math.round((min + range * 0.80) * 100) / 100,
      color: '#22c55e', // green
      label: 'Healthy',
      visible: true,
      chartType: 'default',
      dash: 'dash',
    },
  ];
};

export const useThresholds = (sensorDataArray: RawSensorData[] = []) => {
  const [thresholdsBySensor, setThresholdsBySensor] = useState<Record<string, Threshold[]>>({});

  /**
   * Effect: Normalize incoming raw thresholds and update state if they have changed.
   * Uses mock thresholds as fallback for testing when no thresholds exist.
   */
  useEffect(() => {
    if (!Array.isArray(sensorDataArray)) return;

    const next: Record<string, Threshold[]> = {};

    sensorDataArray.forEach((sensor) => {
      const sensorName = sensor.displayName;
      if (!sensorName) return;

      // Filter backend thresholds - exclude null and undefined values (keep 0)
      const thresholdsArray = Array.isArray(sensor.thresholds) ? sensor.thresholds : [];
      const validBackendThresholds = thresholdsArray.filter(
        (t) => t.value !== null && t.value !== undefined
      );

      // Use backend thresholds if valid ones exist
      if (validBackendThresholds.length > 0) {
        next[sensorName] = validBackendThresholds.map((t, i) => normalizeThreshold(sensorName, t, i));
      }
      // NOTE: Dynamic mock thresholds disabled - now using only real DB thresholds
      // else if (Array.isArray(sensor.y) && sensor.y.length > 0) {
      //   next[sensorName] = createDynamicThresholds(sensorName, sensor.y);
      // }
    });

    setThresholdsBySensor((prev) => {
      return areThresholdMapsEqual(prev, next) ? prev : next;
    });
  }, [sensorDataArray]);

  /**
   * Adds a new threshold to a specific sensor.
   * A unique ID is generated automatically.
   */
  const addThreshold = useCallback((sensorName: string, t: Omit<Threshold, 'id'>) => {
    const newThreshold: Threshold = {
      ...t,
      id: `${sensorName}-threshold-${Date.now()}`,
      sensorName,
    };

    setThresholdsBySensor((prev) => ({
      ...prev,
      [sensorName]: [...(prev[sensorName] || []), newThreshold],
    }));
  }, []);

  /**
   * Removes a threshold by its ID from the specified sensor.
   */
  const removeThreshold = useCallback((sensorName: string, id: string) => {
    setThresholdsBySensor((prev) => ({
      ...prev,
      [sensorName]: prev[sensorName]?.filter((t) => t.id !== id) || [],
    }));
  }, []);

  /**
   * Updates a threshold's properties for a given sensor and ID.
   */
  const updateThreshold = useCallback((sensorName: string, id: string, updates: Partial<Threshold>) => {
    setThresholdsBySensor((prev) => ({
      ...prev,
      [sensorName]: prev[sensorName]?.map((t) => (t.id === id ? { ...t, ...updates } : t)) || [],
    }));
  }, []);

  /**
   * A flat list of all thresholds for all sensors.
   */
  const allThresholds = useMemo(() => Object.values(thresholdsBySensor).flat(), [thresholdsBySensor]);

  return {
    thresholdsBySensor,
    allThresholds,
    addThreshold,
    removeThreshold,
    updateThreshold,
  };
};
